// IconCloseButton.jsx
// -----------------------------------------------------------------------------
// Shared close-affordance button used inside the Character Context hover-panel
// surface: the slider chrome's outer close (CharacterContextPopover) and the
// inner Chapter Activity popup close (HoverPanelMockup → ChapterDialogPopup).
//
// Visual: flat low-opacity glyph at rest; on hover/focus the glyph fades to
// full opacity and a faint accent-tinted pill paints behind it. Browser focus
// rings are suppressed both via CSS (outline:none !important on the class)
// and via `onMouseDown={preventDefault}` so a mouse-click does not leave the
// button in a focused state after release. Keyboard Tab still reaches the
// button — that's the intended a11y path.
//
// The class `.aai-cc-iconbutton` is defined in IconCloseButton.css and is
// imported here once, so any consumer that renders <IconCloseButton/> picks
// up the styling automatically.
//
// API (deliberately thin, this is the only close pattern we ship right now):
//   onClick      required  click handler (forwards from caller)
//   ariaLabel    required  accessible label; close-affordances must be
//                          described for screen-reader users
//   children     optional  glyph; defaults to '×' so the common case is
//                          <IconCloseButton onClick={…} ariaLabel="…" />
//   className    optional  caller can append modifier classes (e.g. layout
//                          hooks) without losing the base class
//   style        optional  inline style override (e.g. position: absolute
//                          for the slider-chrome close, which docks to the
//                          top-right of the popover wrapper). Layout-only
//                          escape hatch; visual treatment should stay in CSS.
//
// `ref` is forwarded so consumers can programmatically focus the button
// (CharacterContextPopover focuses its close on open so keyboard users can
// immediately Tab through the panel or hit Enter to dismiss).
//
// Authored 2026-05-18 (Phase 4D — Hover Panel — styling tweaks session).
// Lives alongside HoverPanelMockup/ and CharacterContextPopover.jsx rather
// than being promoted to src/components/common/; if a third call-site
// outside characterContext/ wants this look, that's the time to promote.
// -----------------------------------------------------------------------------

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './IconCloseButton.css';

const IconCloseButton = forwardRef(function IconCloseButton(
  { onClick, ariaLabel, children = '×', className = '', style },
  ref,
) {
  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      // Suppress mouse-driven focus: clicking the × must not leave the
      // button in a focused state. CSS (outline:none !important) suppresses
      // the ring; this prevents the button from claiming focus in the first
      // place on mouse interactions. Keyboard Tab still focuses normally.
      onMouseDown={(e) => e.preventDefault()}
      aria-label={ariaLabel}
      className={`aai-cc-iconbutton${className ? ` ${className}` : ''}`}
      style={style}
    >
      {children}
    </button>
  );
});

IconCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default IconCloseButton;
